import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";
import { getSettings } from "@/lib/api";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Bestsellers } from "@/components/Bestsellers";
import { ShopSection } from "@/components/ShopSection";
import { TradeSection } from "@/components/TradeSection";
import { ContactSection } from "@/components/ContactSection";
import { Footer } from "@/components/Footer";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import AdminPage from "@/pages/AdminPage";
import TrackPage from "@/pages/TrackPage";
import PoliciesPage from "@/pages/PoliciesPage";
import AccountPage from "@/pages/AccountPage";
import ProductPage from "@/pages/ProductPage";

const Home = () => (
  <div className="min-h-screen">
    <Header />
    <main>
      <Hero />
      <Bestsellers />
      <ShopSection />
      <RecentlyViewed />
      <TradeSection />
      <ContactSection />
    </main>
    <Footer />
  </div>
);

const UnderConstruction = ({ settings }) => {
  const email = settings?.contact_email || "info@sbkmanufacturing.com";
  const phone = settings?.contact_phone || "+91 99607 49074";
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="maintenance-page">
      <div className="max-w-lg text-center">
        <div className="inline-block bg-white rounded-2xl px-6 py-4 shadow-sm border border-border/60">
          <img src="/classina-logo.png" alt="Classina Products" className="h-14 w-auto" />
        </div>
        <p className="mt-10 text-xs uppercase tracking-[0.25em] font-bold text-primary">Launching soon</p>
        <h1 className="mt-4 font-serif text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
          We're getting the shelves <span className="italic text-primary">ready.</span>
        </h1>
        <p className="mt-5 text-muted-foreground leading-relaxed">
          The Classina online store is under construction while we stock it with our full range of notebooks,
          sketch pads and journals. Please check back shortly — or reach us directly for bulk and school orders.
        </p>
        <p className="mt-6 text-sm font-semibold">
          <a href={`mailto:${email}`} data-testid="maintenance-email" className="text-primary hover:underline">{email}</a>
          <span className="text-muted-foreground"> · </span>
          <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} data-testid="maintenance-phone" className="text-primary hover:underline">{phone}</a>
        </p>
      </div>
    </div>
  );
};

const MaintenanceGate = ({ children }) => {
  const [settings, setSettings] = useState(undefined);
  const location = useLocation();

  useEffect(() => {
    getSettings()
      .then(setSettings)
      .catch(() => setSettings(null));
  }, []);

  if (settings === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="app-loading">
        <img src="/classina-logo.png" alt="Classina Products" className="h-12 w-auto animate-pulse" />
      </div>
    );
  }
  if (settings?.under_construction && !location.pathname.startsWith("/admin") && !localStorage.getItem("classina_admin")) {
    return <UnderConstruction settings={settings} />;
  }
  return children;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <CartProvider>
      <BrowserRouter>
        <MaintenanceGate>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/track" element={<TrackPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/product/:slug" element={<ProductPage />} />
          </Routes>
        </MaintenanceGate>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
